import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import { Button } from 'react-bootstrap'

import { FormContainer, NumberInput, ToggleSwitch, NumberInputWithSlider } from 'Shared/Components/FormComponents'
import { showSuccess } from 'Shared/Components/NotifyToast'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import RoiDrawModal from '../RoiDrawModal'

const AIPPE = ({ camera_id, toggle }) => {
  const { t } = useTranslation()
  const { send } = useFetchData()
  const [form] = Form.useForm()
  const { setPageLoading } = useOutletContext()

  const [roi, setRoi] = useState({
    "area": null,
    "snapshot": null
  })

  const [moduleNotFound, setModuleNotFound] = useState(false)
  const [roiModalOpen, setRoiModalOpen] = useState(false)

  const loadConfig = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/ai_detection/configuration/ppe/${camera_id}`,
      returnType: "json"
    })

    if (body == null) {
      setModuleNotFound(true)
      return
    }

    form.setFieldsValue({
      "detection_enabled": body["enabled"],
      "human_conf": body["human_conf"],
      "helmet_enabled": body["helmet_enabled"],
      "helmet_conf": body["helmet_conf"],
      "no_helmet_conf": body["no_helmet_conf"],
      "vest_enabled": body["vest_enabled"],
      "vest_conf": body["vest_conf"],
      "no_vest_conf": body["no_vest_conf"],
      "recording_duration": body["recording_duration"],
      "cool_down_time": body["cool_down_time"],
      "push_alert": body["push_alert"]
    })

    setRoi({
      "area": body["roi"],
      "snapshot": null
    })
  }

  useEffect(() => {
    setPageLoading(true)

    const init = async () => {
      await Promise.all([
        loadConfig()
      ])

      setPageLoading(false)
    }

    init()
  }, [])

  const onFinish = async (values) => {
    setPageLoading(true)

    const res = await send({
      method: "PUT",
      url: `${apiUrl}/ai_detection/configuration/ppe/${camera_id}`,
      contentType: "json",
      body: {
        "enabled": values["detection_enabled"],
        "human_conf": values["human_conf"],
        "helmet_enabled": values["helmet_enabled"],
        "helmet_conf": values["helmet_enabled"] ? values["helmet_conf"] : 0,
        "no_helmet_conf": values["helmet_enabled"] ? values["no_helmet_conf"] : 0,
        "vest_enabled": values["vest_enabled"],
        "vest_conf": values["vest_enabled"] ? values["vest_conf"] : 0,
        "no_vest_conf": values["vest_enabled"] ? values["no_vest_conf"] : 0,
        "recording_duration": values["recording_duration"],
        "cool_down_time": values["cool_down_time"],
        "push_alert": values["push_alert"],
        "roi": JSON.stringify(roi["area"])
      }
    })

    if (res != null) {
      showSuccess(t("general_success"))
      toggle()
    }

    setPageLoading(false)
  }

  const detection_enabled = Form.useWatch('detection_enabled', { form, preserve: true })
  const helmet_enabled = Form.useWatch('helmet_enabled', { form, preserve: true })
  const vest_enabled = Form.useWatch('vest_enabled', { form, preserve: true })

  return (<>
    {moduleNotFound ? (
      <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div><b>Module not found</b></div>
      </div>
    ) : (<>
      <FormContainer form={form} onFinish={onFinish} initialValues={{}}>
        <div style={{ display: "none" }}>
          <ToggleSwitch field_id="detection_enabled" field_name="Detection Enabled"/>  
        </div>

        {detection_enabled && <>
          <NumberInputWithSlider field_id="human_conf" field_name="Human Detection Confidence" required props={{ "min": 0, "max": 1, "step": 0.01, "precision": 2 }}/>

          <ToggleSwitch field_id="helmet_enabled" field_name="Helmet Detection Enabled"/>  

          {helmet_enabled && <>
            <NumberInputWithSlider field_id="helmet_conf" field_name="Helmet Detection Confidence" required props={{ "min": 0, "max": 1, "step": 0.01, "precision": 2 }}/>

            <NumberInputWithSlider field_id="no_helmet_conf" field_name="Without Helmet Detection Confidence" required props={{ "min": 0, "max": 1, "step": 0.01, "precision": 2 }}/>
          </>}

          <ToggleSwitch field_id="vest_enabled" field_name="Vest Detection Enabled"/>  

          {vest_enabled && <>
            <NumberInputWithSlider field_id="vest_conf" field_name="Vest Detection Confidence" required props={{ "min": 0, "max": 1, "step": 0.01, "precision": 2 }}/>

            <NumberInputWithSlider field_id="no_vest_conf" field_name="Without Vest Detection Confidence" required props={{ "min": 0, "max": 1, "step": 0.01, "precision": 2 }}/>
          </>}

          <NumberInput field_id="recording_duration" field_name="Recording Duration (Seconds)" required props={{ "min": 3, "max": 10 }}/>

          <NumberInput field_id="cool_down_time" field_name="Cool Down Time (Seconds)" required props={{ "min": 5, "max": 300 }}/>

          <ToggleSwitch field_id="push_alert" field_name="Push Alert Enabled"/>  

          <Form.Item>
            <Button onClick={() => setRoiModalOpen(true)}>
              {t("Redraw ROI")}
            </Button>
          </Form.Item>
        </>}
      </FormContainer>

      {roiModalOpen && 
        <RoiDrawModal camera_id={camera_id} toggle={() => setRoiModalOpen()} roi={roi["area"]} onRoiChange={(value) => setRoi(value)}/>
      }
    </>)}
  </>)
}

export default AIPPE
